import React, { useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { AppConfigs, AppSkinProps } from '../../../types/decisions';
import BasicTabs from '../../tabs/tabs';
import { getFieldName, scrollToAnchor } from '../../../utils';
import { TriangleSlider } from '../../slider';
import CriteriaSetting from './CriteriaSetting';
import CriteriaSettings from './CriteriaSettings';
import Modal from '../../modal/Modal';
import { useAppConfig } from '../../../context/app-configs-context';
import ScrollShadowBox from '../../ScrollShadowBox/ScrollShadowBox';
import NameWithIcon from './NameWithIcon/NameWithIcon';
import CriteriaSection from './CriteriaSection';

interface CriteriaImportanceAndSettingsProps {
    criteriaFields: AppSkinProps['criteriaData']['fields'];
    onSliderChange: AppSkinProps['onSliderChange'];
    fieldInitialValues: AppSkinProps['fieldInitialValues'];
    onTradeoffValueChange: AppSkinProps['onTradeoffValueChange'];
    onResetTradeOffs: AppSkinProps['onResetTradeOffs'];
    onCriteriaApply: AppSkinProps['onCriteriaApply'];
    externalItems?: AppConfigs['show']['menuItems']['additionalItems'];
}

const CriteriaImportanceAndSettings = ({
    criteriaFields,
    onSliderChange,
    fieldInitialValues,
    onTradeoffValueChange,
    onResetTradeOffs,
    onCriteriaApply,
    externalItems = [],
}: CriteriaImportanceAndSettingsProps): React.JSX.Element => {
    const location = useLocation();
    const isDisabled = location.pathname.startsWith('/patient-choice');
    const [ searchParams ] = useSearchParams();
    const dataset = searchParams.get('dataset') || 'main';
    const { appConfigs: { criteriaSettings: { popup } } } = useAppConfig();
    const [ isImportanceOpen, setIsImportanceOpen ] = useState(true);
    const [ open, setOpen ] = React.useState(false);
    const [ modalContent, setModalContent ] = React.useState<React.ReactElement>(<></>);
    const criteriaChangedOnceRef = useRef<{
        [key: string]: boolean
    }>({});
    const [ activeTab, setActiveTab ] = useState(0);

    const onAutoPopupCheck = (newImportance: number, id: string) => {
        if (!popup.show || newImportance < popup.threshold) {
            return;
        }


        if (!criteriaChangedOnceRef.current[id]) {
            criteriaChangedOnceRef.current[id] = true;
            openCriteriaSettings(id);
        }
    };

    const openCriteriaSettings = (id: string) => {
        const field = criteriaFields.find(item => item.id === id);
        if (!field || field.hide_field) {
            return;
        }

        setModalContent(
            <CriteriaSetting
                field={field}
                fieldInitialValues={fieldInitialValues}
                onTradeoffValueChange={onTradeoffValueChange}
                onSliderChange={onSliderChange}
                handleModalClose={handleClose}
                areCTAsDisplayed
                displayFullWidth
                disabled={isDisabled}
            />,
        );
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setModalContent(<></>);
    };

    return (
        <div>
            <BasicTabs
                tabNames={[ isImportanceOpen ? 'Importance' : 'Settings' ]}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                tabs={[
                    <div className="app-criteria-fields">
                        <ScrollShadowBox>
                           {isImportanceOpen ? (
                                <CriteriaSection
                                    criteriaFields={criteriaFields}
                                    onSliderChange={onSliderChange}
                                    onAutoPopupCheck={onAutoPopupCheck}
                                    openCriteriaSettings={openCriteriaSettings}
                                    getFieldName={getFieldName}
                                />
                                ) : (
                                <CriteriaSettings
                                    criteriaFields={criteriaFields}
                                    onSliderChange={onSliderChange}
                                    onTradeoffValueChange={onTradeoffValueChange}
                                    fieldInitialValues={fieldInitialValues}
                                    handleClose={handleClose}
                                    openCriteriaSettings={openCriteriaSettings}
                                />
                                )}

                        </ScrollShadowBox>
                    </div>,
                ]}
            />
            <div className="app-criteria-actions" style={{ display: 'block' }}>
                <div style={{ marginBottom: 20 }}>
                    <Button
                        className="app-criteria-actions-action"
                        type="submit"
                        variant="outlined"
                        style={{
                            marginRight: '4%',
                            width: '48%',
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            setIsImportanceOpen(isOpen => !isOpen);
                            // the state update is a step behind
                            if (!isImportanceOpen) {
                                scrollToAnchor('app-criteria');
                            }
                        }}
                    >
                        {isImportanceOpen ? 'Settings' : 'Importance'}
                    </Button>
                    <Button
                        className="app-criteria-actions-action"
                        variant="outlined"
                        type="button"
                        onClick={onResetTradeOffs}
                        style={{ width: '48%' }}
                        disabled={isDisabled}
                    >
                        Reset
                    </Button>
                </div>
                <div>
                    <Button
                        className="app-criteria-actions-action"
                        type="submit"
                        variant="contained"
                        style={{ width: '100%' }}
                        onClick={() => onCriteriaApply(dataset)}
                        disabled={isDisabled}
                    >
                        Decide
                    </Button>
                </div>
                {externalItems?.length > 0 && (
                    <div className="additional-menu-items" style={{ marginTop: 32 }}>
                        <h3>External links</h3>
                        <div
                            className="additional-menu-items-content"
                            style={{
                                marginTop: 16,
                                display: 'flex',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                            }}
                        >
                            {externalItems?.map(item => {
                                return (
                                    <Button
                                        key={item.link}
                                        component={RouterLink}
                                        to={item.link}
                                        className="app-criteria-actions-action"
                                        type="button"
                                        href={item.link}
                                        variant="outlined"
                                        sx={{
                                            textTransform: 'none',
                                            width: '48%',
                                            mb: 1,
                                        }}
                                        target="_blank"
                                    >
                                        {item.label}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
            <Modal isOpen={open} handleClose={handleClose}>
                {modalContent}
            </Modal>
        </div>
    );
};

export default CriteriaImportanceAndSettings;